import { useEffect } from 'react';
import { useTabRoute } from '../lib/useTabRoute.ts';
import { PageShell, Section } from '../components/PageShell.tsx';
import { setPageTitle } from '../lib/title.ts';
import {
  BellIcon,
  BoxIcon,
  CodeIcon,
  MessageSquareIcon,
  PlugIcon,
  UsersIcon,
  ZapIcon,
} from '../components/icons.tsx';

/**
 * How the product works, for the people running it.
 *
 * Written against what the code does rather than what the features are called,
 * including the parts that are easy to get wrong — which conversations an agent
 * can see, why a tracked form might not fire, what an API key cannot reach.
 * Help that is merely reassuring is worse than none: it costs a support
 * conversation and the reader's trust at the same time.
 */

const TABS = [
  'overview',
  'inbox',
  'widget',
  'automation',
  'inventory',
  'channels',
  'people',
] as const;
type Tab = (typeof TABS)[number];

/** A paragraph with a heading, which is most of this page. */
function Topic({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="border-t border-slate-200 pt-4 first:border-0 first:pt-0 dark:border-slate-800">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">{title}</h3>
      <div className="mt-1 space-y-2 text-sm text-slate-600 dark:text-slate-300">{children}</div>
    </div>
  );
}

function Steps({ items }: { items: string[] }): React.JSX.Element {
  return (
    <ol className="mt-2 space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
      {items.map((item, index) => (
        <li key={item} className="flex gap-2.5">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[11px] font-semibold text-brand-700 dark:bg-brand-900/60 dark:text-brand-200">
            {index + 1}
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ol>
  );
}

export function HelpPage(): React.JSX.Element {
  const [tab, setTab] = useTabRoute<Tab>('/help', TABS, 'overview');

  // Every route is served the same index.html, so the tab would otherwise read
  // "LeadGab". This one is a public URL people bookmark and paste to each
  // other, and a bookmark named after the product tells them nothing about
  // which page it was.
  //
  // Through lib/title rather than writing document.title: the tab alert has an
  // opinion about the title too, and when both sides captured and restored it
  // they overwrote each other — leaving the tab stuck on a blink frame.
  useEffect(() => {
    setPageTitle('How this works · LeadGab');

    return () => {
      setPageTitle(null);
    };
  }, []);

  return (
    <PageShell
      title="How this works"
      description="What each part does, and the things that are easy to get wrong."
      tabs={[
        {
          id: 'overview',
          label: 'The shape of it',
          icon: MessageSquareIcon,
          help: {
            summary: 'One inbox. Several ways in. Nothing is lost between them.',
            points: [
              'A conversation belongs to a person, not to a channel.',
              'A reply goes out the way it came in.',
              'Everything lives on your own server.',
            ],
          },
        },
        {
          id: 'inbox',
          label: 'Answering',
          icon: BellIcon,
          help: {
            summary: 'Unassigned is the shared queue. Assigned is what you took.',
            points: [
              '“Assign to me” takes a conversation out of the shared queue.',
              'A badge counts conversations with something unread, not messages.',
              'Archiving hides; deleting destroys.',
            ],
          },
        },
        {
          id: 'widget',
          label: 'The widget',
          icon: CodeIcon,
          help: {
            summary: 'One script tag on your site, and the colour is yours.',
            points: [
              'Paste the snippet once, near the end of the page.',
              'Re-copy it if your address ever changes.',
              'It sets no cookies.',
            ],
          },
        },
        {
          id: 'automation',
          label: 'Replies without you',
          icon: ZapIcon,
          help: {
            summary: 'Three different things, often confused for each other.',
            points: [
              'Templates fire on an event. Auto responses fire on a word.',
              'Preset replies do nothing until an agent picks one.',
              'Only the first matching auto response is sent.',
            ],
          },
        },
        {
          id: 'inventory',
          label: 'Inventory',
          icon: BoxIcon,
          help: {
            summary: 'You declare the fields; everything else follows from them.',
            points: [
              'Nothing in the software knows what you sell.',
              'A standing request tells a customer when stock arrives.',
              'An API key can fill it, and reach nothing else.',
            ],
          },
        },
        {
          id: 'channels',
          label: 'Other ways in',
          icon: PlugIcon,
          help: {
            summary: 'SMS, email, Messenger and forms on your own site.',
            points: [
              'Each one continues the same conversation.',
              'Saving Twilio settings also points the number here.',
              'A form is captured only where the widget is loaded.',
            ],
          },
        },
        {
          id: 'people',
          label: 'People and data',
          icon: UsersIcon,
          help: {
            summary: 'Who can do what, and what happens to a conversation in the end.',
            points: [
              'An agent sees unassigned conversations and their own.',
              'Only the owner can delete a conversation.',
              'An unsubscribe goes with the conversation; a block outlives it.',
            ],
          },
        },
      ]}
      active={tab}
      onChange={setTab}
    >
      {tab === 'overview' ? (
        <Section title="The shape of it">
          <div className="space-y-5">
            <Topic title="One conversation, whichever way they wrote">
              <p>
                Somebody writes from the chat widget on your site, texts your number, emails you, or
                messages your Facebook Page. All four land in the same list, and each one is a
                conversation with a person rather than a message in a channel.
              </p>
              <p>
                When you reply, the answer goes back the way it came — you do not have to choose.
                Under the box there is a <strong>Send offline replies via</strong> picker sitting on
                “auto”; it is there for the times you want to force SMS or email, and it is not
                offered on a Messenger thread, which has only one way out.
              </p>
            </Topic>

            <Topic title="If nobody is at the desk">
              <p>
                A visitor who leaves an email address or a phone number can close the tab. Your
                reply reaches them by email or SMS, and the link in that email opens the same
                conversation in their browser — no account, no password.
              </p>
              <p>
                That is the whole point of asking for a contact detail before the chat starts: it is
                what turns a visitor who left into someone you can still answer.
              </p>
              <p>
                In the other direction, a message that arrives while nobody is connected emails you
                and every agent you invited, with a link straight to the conversation. A
                conversation that then sits unanswered is chased once more, later, so it is not
                forgotten because nobody was on shift when it came in.
              </p>
              <p>
                Those two and the tracked-form email are the only three this sends your team on its
                own, and Management → Settings turns each off separately. The rest of what arrives
                from here — an invitation, a password reset, the password for a new account — is
                somebody asking for it, not a notification, and has no switch.
              </p>
            </Topic>

            <Topic title="Where to start">
              <Steps
                items={[
                  'Management → Widget: copy the snippet and paste it on your site.',
                  'Management → Widget: set the colour, and whether to ask for an email, a phone number, or both.',
                  'Management → Templates: change the welcome message to sound like you.',
                  'Management → Team: invite whoever else answers.',
                ]}
              />
            </Topic>
          </div>
        </Section>
      ) : null}

      {tab === 'inbox' ? (
        <Section title="Answering">
          <div className="space-y-5">
            <Topic title="The three tabs">
              <p>
                <strong>Unassigned</strong> is the shared queue — anything nobody has taken.
                <strong> Assigned</strong> is what has been taken, by you or by a colleague.
                <strong> Archived</strong> is what has been put away.
              </p>
              <p>
                An unassigned conversation carries an <strong>Assign to me</strong> button above the
                box. Pressing it moves the conversation to Assigned and out of everyone else's
                queue, so two people do not answer the same person. Replying on its own does not
                take it — you can answer and leave it in the queue if that is what you meant.
              </p>
            </Topic>

            <Topic title="What the badges count">
              <p>
                A number on a tab is how many <em>conversations</em> have something unread in them —
                not how many messages. Five new messages in one conversation is a 1, because what
                you need to know is how many people are waiting.
              </p>
              <p>Nothing is shown at zero, so an empty queue is quiet rather than a row of noughts.</p>
            </Topic>

            <Topic title="Archiving, and deleting">
              <p>
                <strong>Archiving</strong> hides a conversation from the day-to-day list. Everything
                is still there, and a new message from that person brings it back.
              </p>
              <p>
                <strong>Deleting</strong> destroys it: the messages and the images in them, off the
                disk. It cannot be undone, only the owner can do it, and it is in Management →
                Conversations rather than in the inbox, so it is not next to the button you press
                fifty times a day.
              </p>
            </Topic>

            <Topic title="Images">
              <p>
                Attach images with the paperclip, or paste them straight into the box. They reach a
                visitor in the widget as pictures, and an SMS as an MMS.
              </p>
            </Topic>
          </div>
        </Section>
      ) : null}

      {tab === 'widget' ? (
        <Section title="The widget">
          <div className="space-y-5">
            <Topic title="Putting it on your site">
              <p>
                Management → Widget has a snippet: one <code className="lg-code">&lt;script&gt;</code>{' '}
                tag. Paste it into your site's template, before the closing{' '}
                <code className="lg-code">&lt;/body&gt;</code>, on every page you want it. On
                WordPress that is usually the theme footer or a plugin that injects code site-wide.
              </p>
              <p>
                <strong>Re-copy the snippet if the address of this server ever changes.</strong> The
                one you pasted contains the address it was generated with, and nothing here can
                reach into your site to correct it.
              </p>
            </Topic>

            <Topic title="What it asks for">
              <p>
                Before the first message a visitor gives their name and an email address or a phone
                number. You choose which in Management → Widget; at least one has to stay on, because
                without it there is nobody to reply to when they leave.
              </p>
              <p>
                The phone number is what makes SMS follow-up possible, so asking for it is worth the
                extra field if you text people.
              </p>
            </Topic>

            <Topic title="Its colour, and its manners">
              <p>
                The colour applies to the header, the bubble in the corner and the send button, and
                a change shows on your site on the next page load.
              </p>
              <p>
                The widget sets no cookies and carries no analytics or advertising. It keeps one
                identifier for the conversation in the visitor's browser so reopening the chat shows
                what was already said.
              </p>
            </Topic>
          </div>
        </Section>
      ) : null}

      {tab === 'automation' ? (
        <Section title="Replies without you">
          <div className="space-y-5">
            <Topic title="Three things, easily confused">
              <p>
                <strong>Templates</strong> (Management → Templates) fire on an event: a welcome the
                moment a conversation opens, and a note when nobody is connected.
              </p>
              <p>
                The second goes out at most once an hour in any one conversation, so a person
                writing three times in a row is not told three times that nobody is in. Nothing
                else holds it back: a welcome, a stock alert or a delivery notice in the same
                conversation does not count as having already apologised.
              </p>
              <p>
                It reaches a visitor in the widget and nobody else. Somebody who texted, emailed,
                wrote on Messenger or filled in a form is not sitting on the page waiting for it —
                telling them nobody is available would only land in the transcript, where it reads
                as a reply that was never sent.
              </p>
              <p>
                <strong>Auto responses</strong> (Management → Automation) fire on a word. If a
                message contains the phrase, the reply is sent. Only the first match is used, so one
                message never triggers a burst.
              </p>
              <p>
                <strong>Preset replies</strong> do nothing on their own. They are answers you have
                written out, for an agent to drop into the box instead of retyping.
              </p>
            </Topic>

            <Topic title="Writing an auto response worth having">
              <p>
                The phrase is matched anywhere in the message and ignores case, so{' '}
                <code className="lg-code">hours</code> catches "what are your hours?" and "opening
                hours". Short and specific beats clever: a phrase that appears in ordinary
                conversation will fire when you did not mean it to.
              </p>
            </Topic>
          </div>
        </Section>
      ) : null}

      {tab === 'inventory' ? (
        <Section title="Inventory">
          <div className="space-y-5">
            <Topic title="You decide what an item is">
              <p>
                Nothing in the software knows what you sell. On the Fields tab you declare what one
                of your products is made of — a name, a code, a price, a date, whatever you look
                things up by — and everything else follows: the columns in the list, the inputs on
                the add form, and what the search box covers.
              </p>
              <p>
                A field's key is set once, because stored items and customers' standing requests are
                keyed on it. Renaming changes the label only.
              </p>
            </Topic>

            <Topic title="Standing requests">
              <p>
                A standing request is somebody waiting for something you do not have yet. When
                matching stock arrives they are told once — in the conversation they already have,
                and by email if they left an address.
              </p>
              <p>
                The Standing requests tab shows the ones that exist, what each is waiting for, and
                lets you delete one. Creating one is only possible through the API today
                (<code className="lg-code">POST /api/inventory/rules</code>); there is no button for
                it in the app yet.
              </p>
              <p>
                A request with no criteria matches everything. That is allowed on purpose, and it is
                why a large import leaves the notifying to the hourly sweep rather than sending
                hundreds of messages at once.
              </p>
            </Topic>

            <Topic title="Filling it from a script">
              <p>
                The API tab issues keys and shows the exact commands for your own fields. A key
                reaches the inventory and nothing else — not your conversations, not your team, not
                your customers' standing requests, and it cannot issue more keys.
              </p>
              <p>
                Inside the inventory it has the run of the place: it can change or delete a field
                and empty the whole list, not only add items. Give one to a script you trust, and
                revoke it the moment you stop using it.
              </p>
              <p>
                A key is shown once, when you create it. Nothing can show it to you again, so copy
                it then; if you lose it, revoke it and make another.
              </p>
            </Topic>
          </div>
        </Section>
      ) : null}

      {tab === 'channels' ? (
        <Section title="Other ways in">
          <div className="space-y-5">
            <Topic title="SMS">
              <p>
                Management → Channels takes your Twilio account SID, auth token and number. Saving
                does two things at once: it checks the credentials, and it points that number's
                webhook at this server. There is nothing to configure in the Twilio console.
              </p>
            </Topic>

            <Topic title="Email">
              <p>
                Add an address on the Channels tab and mail sent to it opens or continues a
                conversation.
              </p>
              <p>
                Adding it here is only half of it: the server has to be accepting mail as well, and
                it does not by default. That is a job for whoever set the server up — the listener
                is turned on with <code className="lg-code">INBOUND_SMTP_ENABLED</code>, and mail
                normally arrives through the machine's own mail server rather than straight from the
                internet. Adding an address before that is done is harmless; nothing will arrive.
              </p>
            </Topic>

            <Topic title="Facebook Messenger">
              <p>
                Press Connect a page and choose which of your Pages to answer here. A Page belongs
                to one workspace: if a colleague has already connected it, connecting refuses and
                says so rather than quietly taking it over.
              </p>
            </Topic>

            <Topic title="Forms on your own site">
              <p>
                Management → Tracked forms scans a page, and you choose which form to copy. From
                then on, a submission opens a conversation with whoever filled it in — or continues
                the one they already had, matched on the email address or phone number the form
                carried. It arrives in the inbox as somebody to answer, not as a record to file.
                The form itself carries on doing whatever it did before: this listens, it does not
                intercept.
              </p>
              <p>
                Three things have to line up: the widget must be on that page, the address must
                match the one you scanned, and the form must be the one you picked. The address is
                compared as text, so{' '}
                <code className="lg-code">/contact/</code> and <code className="lg-code">/contact</code>{' '}
                are not the same page — turn on "capture it on other pages too" if visitors reach it
                by more than one address.
              </p>
              <p>
                A submission that matches no tracked form, or that carries neither an email address
                nor a phone number, is emailed to the team instead — there is nobody to open a
                conversation with. Management → Settings turns that email off if it is not wanted;
                the submission is captured either way. Management → Tracked forms lists every
                capture and what became of it, which is where to look when one did not arrive as
                expected.
              </p>
            </Topic>
          </div>
        </Section>
      ) : null}

      {tab === 'people' ? (
        <Section title="People and data">
          <div className="space-y-5">
            <Topic title="Who can do what">
              <p>
                An <strong>agent</strong> answers conversations. They see the unassigned queue and
                whatever is assigned to them, and not a colleague's conversations.
              </p>
              <p>
                An <strong>admin</strong> runs the place day to day: every conversation, the team,
                the templates, the tracked forms and the inventory, including its API keys.
              </p>
              <p>
                The <strong>owner</strong> can do all of that and holds the two things an admin
                cannot: the workspace-wide settings under Management — the channels, retention and
                the email notifications — and deleting a conversation, which has no way back.
              </p>
              <p>
                Settings is now only ever about you: your name and picture, whether Enter sends,
                the notification sound and its volume, the theme, your password. Nothing there
                affects anybody else.
              </p>
            </Topic>

            <Topic title="How long conversations stay">
              <p>
                Management → Retention can archive conversations automatically after a period of
                inactivity. Archiving is not deletion — nothing is erased, and a new message brings
                the conversation back.
              </p>
            </Topic>

            <Topic title="What a customer can ask for">
              <p>
                Anyone can ask what is held about them and have it deleted; the{' '}
                <a href="/privacy" className="text-brand-600 hover:underline dark:text-brand-300">
                  privacy page
                </a>{' '}
                explains how, and every notification email carries an unsubscribe link.
              </p>
              <p>
                Deleting a conversation removes its messages, its images and its standing requests
                for good. A <strong>block</strong> deliberately survives it, because blocking
                someone has to outlive the conversation that prompted it.
              </p>
              <p>
                An unsubscribe does not: it belongs to the conversation and goes with it. Deleting
                the conversation of somebody who asked you to stop emailing them therefore clears
                that record — block the address first if the point was to stop writing to them.
              </p>
            </Topic>
          </div>
        </Section>
      ) : null}
    </PageShell>
  );
}
